/* PCIS Health — "Add" modal (opened from the Manage page header).

   Layout (per spec): the COURIER PARENT dropdown is shown first (options from the
   courier-partner parent list, courierPartner/parent/list). Then a toggle:
     • OFF (default) → render the per-courier credential FORM, resolved by the
       selected parent name from the Direct Integration schema (diCourierSchemas.js,
       the same JSON Step 3 uses). Payload carries a `credentials` object.
     • ON → hide the form and show a single ACCOUNT-CODE dropdown listing the
       accounts that exist in PCIS (courier-accounts). Payload carries
       `useCredentialsOf: <that account_code>` and NO `credentials`.
   So there are only two dropdowns: courier parent (always) + the PCIS account
   (only when the toggle is on).

   The new account's identity (account_code, cp_id, display_name) is typed — it
   has no dropdown source in this flow, and the create payload requires it. */
import React, {useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Stack, Switch, Typography} from '@mui/material'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined'
import {SideDrawer, SearchSelect, useToast} from '../components'
import {Field} from '../m0-onboard/steps/fieldKit'
import {resolveCourierSchemaKey, getCourierSchema} from '../m0-onboard/diCourierSchemas'
import {useCreateCourierAccountMutation, useListParentsQuery, useSearchCouriersCachedQuery, useListPcisCourierAccountsQuery} from '../../../redux/supplyApiSlice'

const YN = ['Yes', 'No']

function Labeled({label, invalid, children}) {
  return (
    <div className={'field' + (invalid ? ' invalid' : '')} style={{marginBottom: 0}}>
      <label>{label}</label>
      {children}
      <div className='vmsg'>Required.</div>
    </div>
  )
}
Labeled.propTypes = {label: PropTypes.node, invalid: PropTypes.bool, children: PropTypes.node}

export default function PcisAddAccountModal({open, onClose}) {
  const toast = useToast()
  const [createAccount, {isLoading}] = useCreateCourierAccountMutation()
  const {data: parents} = useListParentsQuery()

  const [courier, setCourier] = useState('') // parent name — drives the credential form schema
  const [accountCode, setAccountCode] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [cpId, setCpId] = useState('')
  const [direction, setDirection] = useState('Forward')
  const [useCreds, setUseCreds] = useState(false)
  const [credsFrom, setCredsFrom] = useState('')
  const [values, setValues] = useState({})
  const [inv, setInv] = useState({})

  /* courier dropdown = courier-partner parent list (same source as onboarding Entry) */
  const courierOptions = useMemo(() => {
    const names = Array.from(new Set((parents || []).map((p) => p.name).filter(Boolean)))
    return names.map((n) => ({value: n, label: n}))
  }, [parents])

  /* account-code source: courierPartner/searchCached returns every courier code
     (the master page joins parent CLIENT-SIDE too). Its records carry the parent
     NAME (`parent`) — not a parentId — so we filter to the selected parent by name. */
  /* prefetch the full courier-code list as soon as the modal opens (skip: !open),
     so it's usually ready before a parent is picked (this call can take 2-3s). */
  const {data: codesRaw, isFetching: codesLoading} = useSearchCouriersCachedQuery(
    {offset: 0, limit: 2000, skipCount: true, andfilter: [{field: 'is_deleted', operator: 'eq', value: '0'}]},
    {skip: !open},
  )
  const nameNorm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '')
  const codes = useMemo(() => {
    const all = Array.isArray(codesRaw) ? codesRaw : (codesRaw && codesRaw.data) || []
    if (!courier) return []
    const wantName = nameNorm(courier)
    return all.filter((c) => c.parent && nameNorm(c.parent) === wantName)
  }, [codesRaw, courier])
  const codeOf = (c) => c.cpAccountCode || c.cp_account_code || c.accountCode || ''

  /* PCIS accounts for the SELECTED parent's courier — one dedicated call
     (GET pcis-admin/courier-accounts?courier=<parent name>), fired as soon as a
     parent is picked. NOT the Manage page's cached list, so future pagination /
     filtering on Manage can never skew what we show here. This single response
     powers BOTH dropdowns: it's the "already in PCIS" set. */
  const {data: pcisForParentRaw} = useListPcisCourierAccountsQuery(courier, {skip: !courier})
  const pcisForParent = useMemo(
    () => (Array.isArray(pcisForParentRaw) ? pcisForParentRaw : (pcisForParentRaw && pcisForParentRaw.data) || []),
    [pcisForParentRaw],
  )
  const existingPcisCodes = useMemo(() => new Set(pcisForParent.map((a) => a.account_code).filter(Boolean)), [pcisForParent])

  /* first dropdown: the parent's codes that are NOT yet in PCIS */
  const accountCodeOptions = useMemo(
    () =>
      codes
        .filter((c) => codeOf(c) && !existingPcisCodes.has(codeOf(c)))
        .map((c) => ({value: codeOf(c), label: c.name ? `${codeOf(c)} — ${c.name}` : codeOf(c)})),
    [codes, existingPcisCodes],
  )
  const pickAccountCode = (v) => {
    setAccountCode(v)
    clr('accountCode')
    const rec = codes.find((c) => codeOf(c) === v)
    if (rec) {
      if (rec.name) setDisplayName(rec.name)
      const cid = rec.cpId ?? rec.cp_id
      if (cid != null && cid !== '') setCpId(String(cid))
    }
  }

  /* copy-from dropdown: the parent's accounts that ARE in PCIS (same response) */
  const pcisAccountOptions = useMemo(
    () =>
      pcisForParent
        .filter((a) => a.account_code && a.account_code !== accountCode)
        .map((a) => ({value: a.account_code, label: a.display_name ? `${a.display_name} (${a.account_code})` : a.account_code})),
    [pcisForParent, accountCode],
  )

  const isShadowfax = /SHADOWFAX/i.test(courier)
  const schema = courier ? getCourierSchema(resolveCourierSchemaKey(courier, direction)) : null

  const clr = (k) => setInv((p) => ({...p, [k]: false}))
  const setVal = (k, v) => {
    setValues((p) => ({...p, [k]: v}))
    clr(k)
  }

  const reset = () => {
    setCourier('')
    setAccountCode('')
    setDisplayName('')
    setCpId('')
    setDirection('Forward')
    setUseCreds(false)
    setCredsFrom('')
    setValues({})
    setInv({})
  }
  const handleClose = () => {
    reset()
    onClose && onClose()
  }

  const submit = async () => {
    const e = {}
    if (!courier) e.courier = true
    if (!String(accountCode).trim()) e.accountCode = true
    if (!String(displayName).trim()) e.displayName = true
    if (!String(cpId).trim()) e.cpId = true
    if (useCreds) {
      if (!credsFrom) e.credsFrom = true
    } else {
      ;(schema?.fields || []).forEach((f) => {
        if (f.type === 'toggle') return
        if (f.required && !String(values[f.key] || '').trim()) e[f.key] = true
      })
    }
    setInv(e)
    if (Object.keys(e).length) return

    /* is_direct: false — an account added here is on Prozo's contract. ProShip Direct accounts (a
       merchant's own contract) are created only from the merchant panel, which sends true. Both
       payload shapes below spread `base`, so this covers the credentials and the use-credentials-of
       paths alike. */
    const base = {
      account_code: accountCode.trim(),
      cp_id: Number(cpId),
      display_name: displayName.trim(),
      is_direct: false,
    }
    let payload
    if (useCreds) {
      payload = {...base, useCredentialsOf: credsFrom}
    } else {
      const credentials = {}
      schema.fields.forEach((f) => {
        credentials[f.key] = f.type === 'toggle' ? !!values[f.key] : values[f.key] || ''
      })
      payload = {...base, credentials}
    }

    try {
      await createAccount(payload).unwrap()
      toast(`Account "${base.account_code}" added.`, 'success')
      handleClose()
    } catch (err) {
      toast(err?.message || 'Could not add the account.', 'error')
    }
  }

  const sectionSx = {border: 1, borderColor: 'divider', borderRadius: '14px', bgcolor: 'background.paper', p: 2.25, mb: 2, boxShadow: '0 1px 2px rgba(15,23,42,.04)'}

  return (
    <SideDrawer
      open={open}
      onClose={handleClose}
      title='Add PCIS Account'
      width={890}
      footer={
        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%'}}>
          <button type='button' className='btn' onClick={handleClose}>
            Cancel
          </button>
          <button
            type='button'
            disabled={isLoading}
            onClick={submit}
            style={{
              border: 'none',
              borderRadius: '10px',
              padding: '9px 22px',
              fontWeight: 700,
              fontSize: 13,
              color: '#fff',
              cursor: isLoading ? 'default' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
              backgroundImage: 'linear-gradient(135deg, #4F7DF0 0%, #2065D1 100%)',
              boxShadow: '0 6px 16px rgba(32,101,209,.35)',
            }}
          >
            {isLoading ? 'Adding…' : 'Add account'}
          </button>
        </div>
      }
    >
      <Typography sx={{fontSize: 12.5, color: 'text.secondary', mb: 2}}>
        Register a courier account into PCIS and choose how its credentials are provided.
      </Typography>

      {/* Section: account */}
      <Box sx={sectionSx}>
        <Stack direction='row' alignItems='center' spacing={1} sx={{mb: 1.75}}>
          <Box sx={{width: 26, height: 26, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(32,101,209,.1)', color: '#2065D1', '& svg': {fontSize: 16}}}>
            <AccountTreeOutlinedIcon />
          </Box>
          <Typography sx={{fontSize: 13, fontWeight: 800, color: 'text.primary'}}>Account</Typography>
        </Stack>

        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 14}}>
          <Labeled label='Courier parent' invalid={inv.courier}>
            <SearchSelect
              placeholder='Select courier parent'
              value={courier}
              invalid={inv.courier}
              onChange={(v) => {
                setCourier(v)
                setValues({})
                setAccountCode('')
                setDisplayName('')
                setCpId('')
                clr('courier')
              }}
              options={courierOptions}
            />
          </Labeled>
          <Labeled label='Account code' invalid={inv.accountCode}>
            <SearchSelect
              placeholder={courier ? (codesLoading ? 'Loading account codes…' : 'Select account code') : 'Pick a parent first'}
              value={accountCode}
              invalid={inv.accountCode}
              onChange={pickAccountCode}
              options={accountCodeOptions}
              disabled={!courier || codesLoading}
              loading={!!courier && codesLoading}
            />
          </Labeled>
        </div>
        <div style={{display: 'grid', gridTemplateColumns: isShadowfax ? '1fr 1fr 1fr' : '1fr 1fr', gap: 16}}>
          <Field label='Display name' value={displayName} onChange={(v) => { setDisplayName(v); clr('displayName') }} ph='e.g. Delhivery Surface - Snitch' invalid={inv.displayName} />
          <Field label='Clickpost ID (CP ID)' type='number' value={cpId} onChange={() => {}} readOnly ph='Auto-filled from account code' invalid={inv.cpId} />
          {isShadowfax ? (
            <Labeled label='Direction'>
              <SearchSelect value={direction} onChange={(v) => setDirection(v || 'Forward')} options={['Forward', 'Reverse']} />
            </Labeled>
          ) : null}
        </div>
      </Box>

      {/* Section: credentials */}
      <Box sx={sectionSx}>
        <Stack direction='row' alignItems='center' spacing={1} sx={{mb: 1.75}}>
          <Box sx={{width: 26, height: 26, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(32,101,209,.1)', color: '#2065D1', '& svg': {fontSize: 16}}}>
            <VpnKeyOutlinedIcon />
          </Box>
          <Typography sx={{fontSize: 13, fontWeight: 800, color: 'text.primary'}}>Credentials</Typography>
        </Stack>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: '10px',
            px: 1.5,
            py: 1.25,
            mb: useCreds || schema ? 2 : 0,
            bgcolor: 'var(--row-alt, #F8FAFC)',
          }}
        >
          <Box sx={{pr: 1}}>
            <Typography sx={{fontSize: 12.5, fontWeight: 700}}>Use credentials of an existing account</Typography>
            <Typography sx={{fontSize: 11, color: 'text.secondary', mt: 0.25}}>Copy credentials from another PCIS account instead of entering them.</Typography>
          </Box>
          <Switch
            checked={useCreds}
            onChange={(ev) => {
              setUseCreds(ev.target.checked)
              setInv({})
            }}
          />
        </Box>

        {useCreds ? (
          <Labeled label='Copy credentials from (PCIS account)' invalid={inv.credsFrom}>
            <SearchSelect
              placeholder='Select an existing account'
              value={credsFrom}
              invalid={inv.credsFrom}
              onChange={(v) => {
                setCredsFrom(v)
                clr('credsFrom')
              }}
              options={pcisAccountOptions}
            />
          </Labeled>
        ) : schema ? (
          <>
            <Typography sx={{fontSize: 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'text.secondary', mb: 1.25}}>
              {schema.label} fields
            </Typography>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px'}}>
              {schema.fields.map((f) =>
                f.type === 'toggle' ? (
                  <Field key={f.key} label={f.label} type='select' options={YN} value={values[f.key] ? 'Yes' : 'No'} onChange={(v) => setVal(f.key, v === 'Yes')} opt />
                ) : (
                  <Field key={f.key} label={f.label} value={values[f.key]} onChange={(v) => setVal(f.key, v)} ph={f.ph} invalid={inv[f.key]} opt={!f.required} />
                )
              )}
            </div>
          </>
        ) : (
          <Box sx={{fontSize: 12.5, color: 'text.secondary', py: 1}}>Select a courier parent to load its credential fields.</Box>
        )}
      </Box>
    </SideDrawer>
  )
}

PcisAddAccountModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
}
